import { useId } from 'react';
import type { FollowPerformanceSummaryDTO } from '@/services/forexApi';

interface ProfitCurveProps {
  summary: FollowPerformanceSummaryDTO | null;
  compact?: boolean;
  hidden?: boolean;
  loading?: boolean;
  error?: boolean;
}

export default function ProfitCurve({ summary, compact = false, hidden = false, loading = false, error = false }: ProfitCurveProps) {
  const gradientId = useId();
  const points = summary?.curve ?? [];
  const verified = Number.isFinite(summary?.total_pnl) && !!summary?.pnl_currency
    && points.length > 0 && points.every(point => typeof point === 'number' && Number.isFinite(point));
  const height = compact ? 48 : 100;
  const className = compact ? 'w-[140px] max-w-full shrink-0' : 'w-full mb-3';

  if (hidden || !verified) {
    const text = hidden ? '收益走势已隐藏' : loading ? '正在加载收益…'
      : error && !summary ? '收益加载失败' : '收益数据待核对';
    return <div role="status" className={`${className} flex items-center justify-center rounded-lg bg-[#F7F8FC] px-2 text-center text-[11px] text-gray-500`} style={{ height }}>{text}</div>;
  }

  // Only scale the server's cumulative closed-P&L series for drawing.
  // Normalizing first avoids overflowing the range for large finite values.
  const scale = points.reduce((largest, point) => Math.max(largest, Math.abs(point)), 1);
  const normalized = points.map(point => point / scale);
  const min = normalized.reduce((smallest, point) => Math.min(smallest, point), Infinity);
  const max = normalized.reduce((largest, point) => Math.max(largest, point), -Infinity);
  const coordinates = normalized.map((point, index) => ({
    x: points.length === 1 ? 170 : index / (points.length - 1) * 340,
    y: max === min ? 50 : 95 - (point - min) / (max - min) * 90,
  }));
  const line = coordinates.map(point => `${point.x},${point.y}`).join(' ');
  const area = `M${coordinates[0].x},100 L${coordinates.map(point => `${point.x},${point.y}`).join(' L')} L${coordinates[coordinates.length - 1].x},100 Z`;

  return <svg role="img" aria-label={`累计已平仓净盈亏曲线（${summary.pnl_currency}）`} className={className}
    height={height} viewBox="0 0 340 100" preserveAspectRatio="none">
    <defs>
      <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#6366F1" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#6366F1" stopOpacity="0.02" />
      </linearGradient>
    </defs>
    {points.length === 1
      ? <circle cx={coordinates[0].x} cy={coordinates[0].y} r="3" fill="#6366F1" />
      : <><path d={area} fill={`url(#${gradientId})`} /><polyline points={line} fill="none" stroke="#6366F1" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" /></>}
  </svg>;
}

export function ProfitReadTime({ readAt, stale }: { readAt: number | null; stale: boolean }) {
  if (readAt === null) return null;
  return <p className={`mt-2 text-[10px] leading-relaxed ${stale ? 'text-amber-700' : 'text-gray-500'}`}>
    最近成功读取：<time dateTime={new Date(readAt).toISOString()}>{new Date(readAt).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })}</time>（UTC+8）
    {stale && ' · 本次刷新失败，保留上次结果'}
  </p>;
}
